/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  AuthenticationErrorMessage,
  ErrorMessage,
  IErrorMessage,
} from "@advapacs/common";
import { ILocalisation } from "@advapacs/common/src/utils/utils";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IPatientConfigurationResponse } from "../../../types/auth";

export interface PortalAccount {
  id: string;
  email: string;
  locale: string;
  name: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  suffix?: string;
  prefix?: string;
  title?: string;
  customerId?: string;
  sessionId?: string;
  mobilePhone?: string;
  sex?: string;
  dob?: string;
  pid?: string;
  localisation: ILocalisation;
  hasSocialAccount?: boolean;
  isRemember?: boolean;
  offerExternalIdentityConnection?: boolean;
}

export interface ILogin {
  channel?: string;
  token?: string;
}

export interface ResendOTPResponse {
  id?: string;
  email?: string;
  mobilePhone?: string;
}

export const initialState = {
  account: null as PortalAccount,
  sessionHasBeenFetched: false as boolean,
  isAuthenticated: false as boolean,
  csrfToken: "" as string,
  loading: false as boolean,
  errorMessage: null as ErrorMessage,
  configuration: null as IPatientConfigurationResponse | null,
  linkError: null as IErrorMessage | null,
  sendOTPLoading: false as boolean,
  sendOTPSuccess: false as boolean,
  resendOTPResponse: null as ResendOTPResponse | null,
  configurationLoaded: false as boolean,
  suspiciousActivity: false as boolean,
  suspiciousActivitySuccess: false as boolean,
  suspiciousActivityResponse: null as any,
  suspiciousActivityFail: false as boolean,
};

const UnauthenticatedSlice = createSlice({
  name: "unauthenticatedReducer",
  initialState,
  reducers: {
    getCsrfTokenStart(state) {
      state.loading = true;
    },
    getCsrfTokenSuccess(state, action: PayloadAction<{ token: string }>) {
      const { token } = action.payload;
      state.csrfToken = token || "";
      state.loading = false;
    },
    getCsrfTokenFail(state, action: PayloadAction<ErrorMessage>) {
      state.errorMessage = action.payload;
      state.loading = false;
      state.csrfToken = "";
    },
    failFetchSession(state) {
      state.sessionHasBeenFetched = true;
      state.errorMessage = null;
      state.loading = false;
      state.isAuthenticated = false;
      state.account = null;
    },
    loginStart(state) {
      state.loading = true;
      state.errorMessage = null;
      state.isAuthenticated = false;
    },
    startFetchSession(state) {
      state.loading = true;
      state.sessionHasBeenFetched = false;
    },
    loginSuccess(state, action: PayloadAction<PortalAccount>) {
      localStorage.setItem("logged", "true");
      state.errorMessage = null;
      state.loading = false;
      state.isAuthenticated = true;
      state.account = action.payload;
      state.sessionHasBeenFetched = true;
    },
    loginFail(
      state,
      action: PayloadAction<{
        err: AuthenticationErrorMessage;
        pathname: string;
      }>
    ) {
      const { err } = action.payload;
      localStorage.removeItem("logged");
      state.errorMessage = err;
      state.loading = false;
      state.isAuthenticated = false;
      state.account = null;
      state.sessionHasBeenFetched = true;
    },
    clearAuthentication(state) {
      state.errorMessage = null;
      state.sendOTPSuccess = false;
      state.loading = false;
      state.isAuthenticated = false;
      state.account = null;
      state.sessionHasBeenFetched = false;
      localStorage.removeItem("logged");
      localStorage.removeItem("remember"); // remove remember when logout
      localStorage.removeItem("skip_in_session");
      localStorage.removeItem("newAccessId"); // Remove new access id.
      window.location.href = "/";
    },
    clearAuthenticationWhenDeclineEULA(state) {
      state.sendOTPSuccess = false;
      state.loading = false;
      state.isAuthenticated = false;
      state.account = null;
      state.sessionHasBeenFetched = false;
      localStorage.removeItem("logged");
      localStorage.removeItem("remember"); // remove remember when logout
      window.location.href = `/t/${localStorage.getItem(
        "pp-token"
      )}/?eulaWarnMessage=true`;
    },
    getConfigurationStart(state) {
      state.configurationLoaded = false;
    },
    getConfigurationSuccess(state, action) {
      state.configuration = {
        ...action.payload,
      };
      state.configurationLoaded = true;
    },
    getConfigurationFail(state, action) {
      state.linkError = action.payload;
      state.configurationLoaded = true;
    },
    getOTPTypeResponseStart(state) {
      state.sendOTPLoading = true;
      state.sendOTPSuccess = false;
    },
    getOTPTypeResponseSuccess(state, action) {
      state.sendOTPLoading = false;
      state.resendOTPResponse = action.payload;
      state.sendOTPSuccess = true;
      state.suspiciousActivity = false;
    },
    getOTPTypeResponseFail(state, action) {
      state.sendOTPLoading = false;
      state.errorMessage = action.payload;
      state.sendOTPSuccess = false;
    },
    stopLoading(state) {
      state.resendOTPResponse = null;
      state.sendOTPLoading = false;
      state.sendOTPSuccess = false;
    },
    clearSendOTPMethod(state) {
      state.resendOTPResponse = null;
      state.sendOTPSuccess = false;
      state.sendOTPLoading = false;
      state.errorMessage = null;
    },
    clearErrorMessage(state) {
      state.errorMessage = null;
    },
    suspicousActivityStart(state) {
      state.suspiciousActivity = true;
    },
    suspicousActivitySuccess(state, action) {
      state.suspiciousActivityResponse = action.payload;
      state.suspiciousActivitySuccess = true;
      state.suspiciousActivityFail = false;
    },
    getOTPTypeResponseSuccessFromSuspiciousActivitySuccess(state) {
      state.sendOTPLoading = false;
      state.resendOTPResponse = state.suspiciousActivityResponse;
      state.sendOTPSuccess = true;
    },
    suspicousActivityFail(state) {
      state.suspiciousActivityFail = true;
      state.suspiciousActivitySuccess = false;
      state.suspiciousActivityResponse = null;
    },
    clearSuspicousActivity(state) {
      state.suspiciousActivity = false;
      state.suspiciousActivityFail = false;
      state.suspiciousActivitySuccess = false;
      state.suspiciousActivityResponse = null;
    },
    setOfferExternalIdentityConnectionIsFalse(state) {
      state.account = {
        ...state.account,
        offerExternalIdentityConnection: false,
      };
    },
    setErrorMessage(state, action) {
      state.errorMessage = action.payload;
    },
    resetLoadingState(state) {
      state.sendOTPLoading = false;
      state.loading = false;
      state.sendOTPSuccess = false;
    },
    setLoadingState(state) {
      // just clear loading state
      state.sendOTPLoading = false;
      state.loading = false;
    },
  },
});

export const UnauthenticatedActions = UnauthenticatedSlice.actions;
export default UnauthenticatedSlice.reducer;
